import { useMemo, useState } from 'react'
import { Link } from 'react-router-dom'
import DocLayout from '../components/docs/DocLayout'
import { MissionPreview } from '../components/home/MissionsShowcase'
import { america, ancestorsOf, missions, parentOf, type Mission } from '@/content/missions'
import { usePrefs } from '@/state/prefs'
import Cased from '@/components/ui/Cased'

const kindLabel: Record<Mission['kind'], string> = {
  nation: 'Default theme',
  department: 'Executive department',
  independent: 'Independent agency',
  subagency: 'Agency',
}

const filters = [
  { id: 'all', label: 'All' },
  { id: 'department', label: 'Departments' },
  { id: 'independent', label: 'Independent' },
  { id: 'subagency', label: 'Sub-agencies' },
] as const

/** The label under a mission's name: its kind, or the department it belongs to. */
function describe(mission: Mission) {
  const parent = parentOf(mission)
  const kind = parent ? `Part of ${parent.short}` : kindLabel[mission.kind]
  return mission.established ? `${kind} · Est. ${mission.established}` : kind
}

/** Search across names, short names and the names of the agencies a mission sits within. */
function matches(mission: Mission, words: string[]) {
  const family = ancestorsOf(mission).map((ancestor) => `${ancestor.name} ${ancestor.short}`)
  const haystack = `${mission.name} ${mission.short} ${family.join(' ')}`.toLowerCase()
  return words.every((word) => haystack.includes(word))
}

export default function MissionsPage() {
  const { prefs, set } = usePrefs()
  const active = prefs.mission !== america.id
  const [filter, setFilter] = useState<(typeof filters)[number]['id']>('all')
  const [query, setQuery] = useState('')
  const shown = useMemo(() => {
    const words = query.trim().toLowerCase().split(/\s+/).filter(Boolean)
    return missions.filter(
      (mission) => (filter === 'all' || mission.kind === filter) && matches(mission, words),
    )
  }, [filter, query])
  return (
    <DocLayout
      eyebrow="Missions"
      title="A theme for every mission."
      wide
      intro={
        <>
          <p>
            Each mission theme starts from an agency's own colors, taken from its brand guide, its website or its seal.
            MDS derives a full set of light and dark color roles from that palette and adjusts each one until every
            pairing meets WCAG AA.
          </p>
          <p>
            Themes are generated at build time and published as CSS and JSON. Applying one is a single attribute on the
            page; nothing is computed in the browser.
          </p>
        </>
      }
    >
      <section className="msn-section" aria-labelledby="msn-gallery">
        <div className="msn-gallery__head">
          <h2 id="msn-gallery" className="mds-h2"><Cased>Mission themes</Cased></h2>
          {active && (
            <button type="button" className="mds-button mds-button--secondary mds-button--sm" onClick={() => set('mission', america.id)}>
              Reset to America
            </button>
          )}
        </div>
        <div className="msn-filters">
          <div className="mds-segmented" role="group" aria-label="Filter missions">
            {filters.map((option) => (
              <button
                key={option.id}
                type="button"
                className="mds-segmented__option"
                aria-pressed={filter === option.id}
                onClick={() => setFilter(option.id)}
              >
                {option.label}
              </button>
            ))}
          </div>
          <label className="msn-filters__search">
            <span className="mds-visually-hidden">Search missions</span>
            <input
              className="mds-input"
              type="search"
              placeholder="Search agencies"
              value={query}
              onChange={(event) => setQuery(event.target.value)}
            />
          </label>
        </div>
        <p className="mds-muted msn-filters__count" role="status">
          {shown.length === 1 ? '1 mission' : `${shown.length} missions`}
        </p>
        <ul className="msn-gallery">
          {shown.map((mission) => {
            const applied = prefs.mission === mission.id
            return (
              <li key={mission.id} className="msn-gallery__item">
                <MissionPreview mission={mission} />
                <div className="msn-gallery__meta">
                  <h3 className="mds-card__title">
                    <Link to={`/missions/${mission.id}`} className="msn-gallery__link">
                      {mission.name}
                    </Link>
                  </h3>
                  <p className="mds-muted">{describe(mission)}</p>
                </div>
                <div className="mds-cluster">
                  <button
                    type="button"
                    className="mds-button mds-button--primary mds-button--sm"
                    aria-pressed={applied}
                    onClick={() => set('mission', mission.id)}
                  >
                    {applied ? 'Applied' : 'Apply'}
                    <span className="mds-visually-hidden"> {mission.name} theme</span>
                  </button>
                  <Link to={`/missions/${mission.id}`} className="mds-button mds-button--ghost mds-button--sm">
                    Details<span className="mds-visually-hidden"> for {mission.name}</span>
                  </Link>
                </div>
              </li>
            )
          })}
        </ul>
      </section>
      <section className="doc-section" aria-labelledby="msn-how">
        <h2 id="msn-how"><Cased>How a theme is made</Cased></h2>
        <ol>
          <li>Record the agency palette (primary, secondary, accent) with the source it came from.</li>
          <li>Derive the light and dark roles: surfaces tinted toward the primary, ink from the secondary.</li>
          <li>Adjust each role in small steps until text meets 4.5:1 and controls meet 3:1 on every surface.</li>
          <li>
            Publish the result to <code>/mds/themes/</code> and fail the build if any pairing regresses.
          </li>
        </ol>
      </section>
    </DocLayout>
  )
}
